import {
  Box,
  Button,
  Grid,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { OnboardingStepper } from "../components/OnboardingStepper";
import { SliderWithInput } from "../components/SliderWithInput.tsx";
import {
  commercialAndIndustrialOptions,
  industryTypeOptionsByGroup,
} from "../data/industryTypeOptions.ts";
import { EmissionsBySource } from "@labs/shared/types/EmissionsBySource.ts";
import { IndustryType } from "@labs/shared/types/IndustryType.ts";
import { useLocalBaselineEmissionsState } from "../util/useLocalBaselineEmissionsState.ts";
import { useLocalUserState } from "../util/useLocalUserState.ts";
import { onboardingSteps } from "../data/onboardingSteps.ts";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { trackEvent } from "../util/analytics";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import { IndustryTypeAndBaselineFormType } from "../types/IndustryTypeAndBaselineFormType.ts";
import { HeroHeader } from "../components/HeroHeader.tsx";
import { LabsCard } from "../components/LabsCard.tsx";
import { LabsDropdown } from "../components/LabsDropdown.tsx";
import { NavigationButtons } from "../components/NavigationButtons.tsx";
import omit from "ramda/es/omit";
import { TotalPercentageDisplay } from "../components/TotalPercentageDisplay.tsx";
import emissionsImg from "../assets/banners/emissionProf.png";

const BaselinePieChart = lazy(() => import("../charts/BaselinePieChart"));

const BaselineSliderWithInput =
  SliderWithInput<IndustryTypeAndBaselineFormType>;

const BaselineDropdown = LabsDropdown<IndustryTypeAndBaselineFormType>;

const calculateTotalPercentage = (
  data: Record<string, number | undefined>,
): number =>
  Object.values(data).reduce<number>((sum, value) => sum + (value ?? 0), 0);

const BaselineForm = () => {
  const [user, setUser] = useLocalUserState();
  const [baselineEmissions, setBaselineEmissions] =
    useLocalBaselineEmissionsState();
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<IndustryTypeAndBaselineFormType>({
    mode: "onChange",
    defaultValues: {
      ...baselineEmissions,
      industryType: user.industryType,
    },
  });
  const baselineFormValues = useWatch({ control });

  const baselineEmissionsFromFormWithoutIndustryType = useMemo(
    () => omit(["industryType"], baselineFormValues),
    [baselineFormValues],
  );

  const navigate = useNavigate();

  const [hoveredCharts] = useState(new Set<string>());

  useEffect(() => {
    trackEvent(EventCategory.Baseline, "Start");
  }, []);

  const onIndustryTypeChange = (e: SelectChangeEvent) => {
    const value = e.target.value as IndustryType;
    setUser((prevUser) => ({
      ...prevUser,
      industryType: value,
    }));
    trackEvent(EventCategory.Baseline, "Change Field Value", "Industry", value);
    const selectedIndustry = commercialAndIndustrialOptions.find(
      (option) => option.value === value,
    );
    if (selectedIndustry) {
      Object.entries(selectedIndustry.baselineEmissions).forEach(
        ([name, value]) => {
          setValue(name as keyof EmissionsBySource, value);
        },
      );
    }
  };

  const onReset = () => {
    trackEvent(EventCategory.Baseline, "Reset");
    const selectedIndustry = commercialAndIndustrialOptions.find(
      (option) => option.value === baselineFormValues.industryType,
    );
    if (selectedIndustry) {
      Object.entries(selectedIndustry.baselineEmissions).forEach(
        ([name, value]) => {
          setValue(name as keyof EmissionsBySource, value);
        },
      );
    }
  };

  const totalPercentage = calculateTotalPercentage(
    omit(["industryType"], baselineFormValues),
  );

  const prevValidState = useRef<boolean>(true);
  const isValid = totalPercentage === 100;

  useEffect(() => {
    if (prevValidState.current !== isValid) {
      trackEvent(
        EventCategory.Baseline,
        isValid ? "Exit Invalid State" : "Enter Invalid State",
      );
      prevValidState.current = isValid;
    }
  }, [isValid]);

  return (
    <Stack direction={"column"} spacing={4}>
      <OnboardingStepper activeStep={1} steps={onboardingSteps} />
      <form
        onSubmit={handleSubmit((data) => {
          trackEvent(EventCategory.Baseline, "Complete");
          setBaselineEmissions(data);
          navigate("/plan");
        })}
      >
        <Grid container spacing={4}>
          <Grid size={12}>
            <HeroHeader
              title="Emissions Profile"
              description={
                "Enter your organization's scope 1 and 2 emissions profile for your base year. Don't worry if this profile is not 100% precise. Getting close at this stage will still help you iterate on a plan."
              }
              backgroundImage={emissionsImg}
              showCalculateLink
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabsCard>
              <Typography variant={"h6"} color={"primary.dark"}>
                Select your industry to get started
              </Typography>
              <BaselineDropdown
                control={control}
                optionsByGroup={industryTypeOptionsByGroup}
                name={"industryType"}
                label={"Industry"}
                onChange={onIndustryTypeChange}
                errors={errors}
              />
              <Typography variant={"body1"} color={"primary.dark"}>
                Baseline Emissions
              </Typography>
              <Suspense fallback={<LoadingIndicator />}>
                <BaselinePieChart
                  baselineEmissions={
                    baselineEmissionsFromFormWithoutIndustryType
                  }
                  showEditLink={false}
                  hoveredCharts={hoveredCharts}
                  analyticsCategory={EventCategory.Baseline}
                />
              </Suspense>
            </LabsCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabsCard>
              <Stack direction={"column"} spacing={2}>
                <Box>
                  <Typography variant={"h6"} color={"primary.dark"}>
                    Customize your emissions profile
                  </Typography>
                  <Typography variant={"body2"} sx={{ mb: 2 }}>
                    Enter the contribution of each emissions source to your base
                    year scope 1 + 2 emissions footprint.
                  </Typography>
                  <BaselineSliderWithInput
                    name="electricity"
                    label="Electricity"
                    control={control}
                  />
                  <BaselineSliderWithInput
                    name="naturalGas"
                    label="Natural Gas"
                    control={control}
                  />
                  <BaselineSliderWithInput
                    name="diesel"
                    label="Diesel"
                    control={control}
                  />
                  <BaselineSliderWithInput
                    name="gasoline"
                    label="Gasoline"
                    control={control}
                  />
                  <BaselineSliderWithInput
                    name="propane"
                    label="Propane"
                    control={control}
                  />
                  <BaselineSliderWithInput
                    name="fuelOil"
                    label="Fuel Oil"
                    control={control}
                  />
                  <BaselineSliderWithInput
                    name="refrigerants"
                    label="Refrigerants"
                    control={control}
                  />
                  <BaselineSliderWithInput
                    name="other"
                    label="Other"
                    control={control}
                  />
                </Box>
                <Box>
                  <Stack
                    spacing={4}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={onReset}
                    >
                      Reset
                    </Button>
                    {totalPercentage !== 100 && (
                      <Typography variant="body2" color="error">
                        The total emissions percentage must equal 100%.
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <TotalPercentageDisplay totalPercentage={totalPercentage} />
                </Box>
              </Stack>
            </LabsCard>
          </Grid>
          <Grid size={12}>
            <NavigationButtons
              backHref={"/target"}
              backEnabled
              nextEnabled={totalPercentage === 100}
            />
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
};

export default BaselineForm;
